import React, { useState, useContext, useEffect } from 'react';
import { Form, Select, DatePicker, Checkbox, Switch, Button as AntdButton, Divider } from 'antd';
import dayjs from 'dayjs';
import axios from '@common/axios';
import GenericDialog from "@common/GenericDialog";
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import { BrandContext } from "@context/BrandContext";
import { UserContext } from "@context/UserContext";
import TableAntD from "@common/TableAntD";

const { RangePicker } = DatePicker;

const GenerateActivityReportSummaryDialog = ({
    visible,
    handleClose,
    onGenerateSuccess,
    selectedClassId,
    initialStartDate,
    initialEndDate,
    initialMetrics,
    initialExcludeInactive,
    initialIncludeBelowAverage
}) => {
    const {loadShortcutByUrl} = useCurrentShortcut();
    const brandConfig = useContext(BrandContext);
    const { user } = useContext(UserContext);
    const [form] = Form.useForm();

    const [selectedMetrics, setSelectedMetrics] = useState(initialMetrics?.length > 0 ? initialMetrics : ['all']);
    const [metricsOpen, setMetricsOpen] = useState(false);
    const [dateRange, setDateRange] = useState([
        initialStartDate || dayjs().subtract(6, 'day'),
        initialEndDate || dayjs()
    ]);
    const [excludeInactiveUsers, setExcludeInactiveUsers] = useState(initialExcludeInactive || false);
    const [includeBelowAverage, setIncludeBelowAverage] = useState(initialIncludeBelowAverage !== false);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [teacherClasses, setTeacherClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [showAllClasses, setShowAllClasses] = useState(false);
    const [allSchoolClasses, setAllSchoolClasses] = useState([]);
    const [classError, setClassError] = useState(false);

    const presetRanges = {
        Today: [dayjs(), dayjs()],
        'This Week': [dayjs().startOf('week'), dayjs().endOf('week')],
        'Last 7 Days': [dayjs().subtract(6, 'day'), dayjs()],
        'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
    };

    const metricOptions = [
        { label: 'All', value: 'all' },
        { label: 'Sessions Started', value: 'sessionsStarted' },
        { label: 'Last Login', value: 'lastLogin' },
        { label: 'Exercises Complete', value: 'exercisesComplete' },
        { label: 'Walkthroughs Complete', value: 'walkthroughsComplete' },
        { label: 'Section Quizzes Complete', value: 'quizzesComplete' },
        { label: 'Questions Complete', value: 'quizQuestions' },
        { label: `${brandConfig?.testsName || 'Tests'} Complete`, value: 'testsComplete' },
        { label: 'Scorchers Complete', value: 'scorchersComplete'}
    ].filter(option => {
        if (brandConfig?.brandCode === 'DYNAMICENGLISH') {
            return option.value !== 'exercisesComplete' && option.value !== 'walkthroughsComplete';
        }
        return true;
    });

    const handleMetricsChange = (newValues) => {
        // If "All" is being selected
        if (newValues.includes('all') && !selectedMetrics.includes('all')) {
            setSelectedMetrics(['all']);
        }
        // If other options are being selected while "All" is selected
        else if (selectedMetrics.includes('all') && newValues.length > 1) {
            setSelectedMetrics(newValues.filter(v => v !== 'all'));
        }
        // Normal selection
        else {
            setSelectedMetrics(newValues);
        }
    };

    useEffect(() => {
        if (visible) {
            // Reset date range to initial values when dialog opens
            setDateRange([
                initialStartDate || dayjs().subtract(6, 'day'),
                initialEndDate || dayjs()
            ]);
        }
    }, [visible, initialStartDate, initialEndDate]);

    useEffect(() => {
        if (visible && !selectedClassId) {
            loadClasses();
        }
    }, [visible, selectedClassId]);

    useEffect(() => {
        if (showAllClasses) {
            loadAllSchoolClasses();
        }
    }, [showAllClasses]);

    useEffect(() => {
        if (visible && selectedClassId) {
            fetchStudentComments();
        }
    }, [visible, selectedClassId, dateRange]);

    const loadClasses = async () => {
        try {
            const response = await fetch('/controlCentre/getTeacherClasses.action');
            const data = await response.json();

            const classOptions = data.classes.map(cls => ({
                label: cls.className,
                value: cls.classId,
            }));

            setTeacherClasses(classOptions);
        } catch (error) {
            console.error('Failed to load teacher classes:', error);
        }
    };

    const loadAllSchoolClasses = async () => {
        try {
            const response = await fetch('/web/ui/sc/classes');
            const data = await response.json();

            const classOptions = Object.values(data).map(cls => ({
                label: cls.className,
                value: cls.classId || cls.id,
            }));

            setAllSchoolClasses(classOptions);
        } catch (error) {
            console.error('Failed to load all school classes:', error);
        }
    };

    const handleShowAllClassesChange = (checked) => {
        setShowAllClasses(checked);
        if (!checked) {
            setSelectedClass(null);
        }
    };

    const getAvailableClasses = () => {
        return showAllClasses ? allSchoolClasses : teacherClasses;
    };

    const renderClassSelectDropdown = (menu) => (
        <div>
            <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <Switch
                    checked={showAllClasses}
                    onChange={handleShowAllClassesChange}
                    size="small"
                />
                <span style={{ fontSize: '14px' }}>Show all classes</span>
            </div>
            {menu}
        </div>
    );

    const renderMetricsDropdown = (menu) => (
        <div>
            {menu}
            <div style={{ padding: '4px 8px', boxShadow: '0 -2px 6px rgba(0, 0, 0, 0.1)' }}>
                <AntdButton type="primary" block size="small" onClick={() => setMetricsOpen(false)}>
                    OK
                </AntdButton>
            </div>
        </div>
    );

    const fetchStudentComments = () => {
        setLoading(true);
        const [start, end] = dateRange || [];

        const isUSLocale = user.locale === 'en_US';

        const formatDate = (date) => {
            const day = date.date().toString().padStart(2, '0');
            const month = (date.month() + 1).toString().padStart(2, '0');
            const year = date.year();
            return isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
        };

        axios.get('/web/ui/report/teacher/activity/classStudentComments', {
            params: {
                classId: selectedClassId,
                startSearch: formatDate(start),
                endSearch: formatDate(end),
                searchPeriod: 'DATE_RANGE'
            }
        })
            .then(response => {
                console.log('Class Student Comments:', response.data);
                setStudents(response.data?.students || []);
            })
            .catch(error => {
                console.error('Error fetching student comments:', error);
                setStudents([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleGenerate = () => {
        const params = new URLSearchParams();

        // Use selectedClass if no selectedClassId was provided
        const classId = selectedClassId || selectedClass;

        if (!classId) {
            setClassError(true);
            return;
        }

        // Only add metrics if NOT "all"
        if (!selectedMetrics.includes('all') && selectedMetrics.length > 0) {
            params.set('metrics', selectedMetrics.join(','));
        }

        // Only add date range if it's NOT the default (last 7 days)
        if (dateRange && dateRange[0] && dateRange[1]) {
            const defaultStart = dayjs().subtract(6, 'day');
            const defaultEnd = dayjs();
            const isDefault = dateRange[0].isSame(defaultStart, 'day') && dateRange[1].isSame(defaultEnd, 'day');

            if (!isDefault) {
                params.set('startDate', dateRange[0].format('YYYY-MM-DD'));
                params.set('endDate', dateRange[1].format('YYYY-MM-DD'));
            }
        }

        // Add exclude filter
        if (excludeInactiveUsers) {
            params.set('excludeInactive', 'true');
        }

        // Add below average comments filter (default is true, so only add param if false)
        if (!includeBelowAverage) {
            params.set('includeBelowAverage', 'false');
        }

        // Determine the correct URL based on where the dialog was started from
        const currentPath = window.location.pathname;
        let url;
        if (currentPath.includes('/web/ui/view/reports/activitySummaryManager')) {
            url = `/web/ui/view/reports/activitySummaryManager/${classId}/activitySummary?${params.toString()}`;
        } else {
            url = `/web/ui/view/reports/activityReport/${classId}/activitySummary?${params.toString()}`;
        }

        // Check if we're already on this URL (same path and params)
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        const newUrl = url;

        if (currentUrl === newUrl && onGenerateSuccess) {
            // Same URL - trigger refetch instead of navigating
            onGenerateSuccess();
        } else {
            // Different URL - navigate
            loadShortcutByUrl(url);
            handleClose();
        }
    };

    return (
        <GenericDialog
            title="Generate Summary Comments"
            open={visible}
            dialogWidth={450}
            onClose={handleClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: {type: 'text'}
                },
                {
                    name: 'Generate',
                    callback: handleGenerate,
                    extraProps: {
                        type: 'primary'
                    }
                }
            ]}
        >
            <p>Choose from the options below to automatically generate summaries for your students</p>
            <Form
                form={form}
                layout="vertical"
                className={'ch-form'}
            >
                {!selectedClassId && (
                    <Form.Item
                        label="Class"
                        validateStatus={classError ? 'error' : ''}
                        help={classError ? 'Please select a class' : ''}
                    >
                        <Select
                            placeholder="Select class"
                            options={getAvailableClasses()}
                            value={selectedClass}
                            onChange={(value) => {
                                setSelectedClass(value);
                                setClassError(false);
                            }}
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            popupRender={renderClassSelectDropdown}
                        />
                    </Form.Item>
                )}

                <Form.Item label="Metrics">
                    <Select
                        mode="multiple"
                        placeholder="Select metrics"
                        options={metricOptions}
                        value={selectedMetrics}
                        onChange={handleMetricsChange}
                        allowClear={true}
                        open={metricsOpen}
                        onOpenChange={setMetricsOpen}
                        popupRender={renderMetricsDropdown}
                    />
                </Form.Item>

                <Form.Item label="Date range">
                    <RangePicker
                        presets={Object.entries(presetRanges).map(([label, range]) => ({
                            label,
                            value: range,
                        }))}
                        value={dateRange}
                        onChange={setDateRange}
                        format={user?.getDateFormatString?.() || 'DD/MM/YYYY'}
                        style={{width: '100%'}}
                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                    />
                </Form.Item>

                <Form.Item label={'Student options'}>
                    <Checkbox
                        checked={excludeInactiveUsers}
                        onChange={(e) => setExcludeInactiveUsers(e.target.checked)}
                    >
                        Exclude students who have not logged in
                    </Checkbox>
                </Form.Item>
                <Form.Item label={'Improvement insights'}>
                    <Checkbox
                        checked={includeBelowAverage}
                        onChange={(e) => setIncludeBelowAverage(e.target.checked)}
                    >
                        Include metrics which indicate where a student could improve their focus
                    </Checkbox>
                </Form.Item>
            </Form>
        </GenericDialog>
    );
};

export default GenerateActivityReportSummaryDialog;
